/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { ICustomer } from "../../../../interfaces/customers";
import { useParams } from "react-router-dom";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { handleUpload } from "../../../../utils/handleUpload";
import { createCustomer, createFile, listCustomers, showCustomer, updateCustomer, deleteCustomer } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const defaultCustomer: ICustomer = {
  name: "",
  nameFantasy: "",
  description: "",
  active: true,
  document: "",
  email: "",
  address: "",
  complement: "",
  createdIn: undefined,
};

export const useCustomers = ({
  disabledShowId,
  disabledGetAll,
  filter: filterInitial,
}: {
  filter?: Record<string, any>;
  disabledShowId?: boolean;
  disabledGetAll?: boolean;
}) => {
  const { idCustomer: id } = useParams();
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [customer, setCustomer] = useState<ICustomer>(defaultCustomer);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit: 25 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/customers" });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCustomer = (key: string, value: any) => {
    setCustomer((curr) => {
      const newData = updateValue(curr, key, value);
      return { ...curr, ...newData };
    });
  };

  const uploadImagem = (
    fileUpload: any,
    onProgress?: (data: number) => void
  ) => {
    setLoading(true);
    createFile({ file: fileUpload })
      .then(({ file: fileCreate }) => {
        handleUpload({
          dataUpload: { urlUpload: fileCreate.urlUpload, ...fileUpload },
          onProgress,
        }).then(async () => {
          await updateCustomer({
            id: customer.id!,
            customer: { idFilePhoto: fileCreate.id },
          });
        });
      })
      .finally(() => setLoading(false));
  };

  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter,
        sort,
      }: {
        page?: number;
        limit?: number;
        filter?: any;
        sort?: any;
      }) => {
        setLoading(true);
        listCustomers({
          includes: ["createdBy", "updatedBy"],
          page,
          filter: filter,
          limit,
          sort,
        })
          .then(
            ({ customers: listCustomers, limit, currentPage, totalItems }) => {
              setCustomers(listCustomers);
              
              setLimit(Number(limit));
              setTotalItems(Number(totalItems));
            }
          )
          .finally(() => setLoading(false));
      },
      150
    ),
    []
  );

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showCustomer({ id })
      .then(({ customer: customersShow }) => {
        setCustomer(customersShow);
      })
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({ list: listCustomers, key: "customers", filter });

  const create = () => {
    setLoading(true);
    createCustomer({ customer })
      .then(({ customer: customerCreate }) => {
        toast.success(`Cliente (${customerCreate.name}) criado com sucesso!`);
        goEdit(customerCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateCustomer({ id: customer.id!, customer })
      .then(({ customer: customerUpdate }) => {
        toast.success(
          `Cliente (${customerUpdate.name}) atualizado com sucesso!`
        );
        goEdit(customerUpdate.id!);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter, sort });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteCustomer({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Cliente",
        plural: "clientes",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    customers,
    customer,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleCustomer,
    create,
    id,
    update,
    setTotalItems,
    totalItems,
    handleFilter,
    handleSort,
    search,
    uploadImagem,
  };
};
